import { Component, Input, OnChanges, SimpleChanges, inject } from '@angular/core';
import { DomSanitizer, type SafeResourceUrl } from '@angular/platform-browser';
import { OEP_ENTITY, OEP_LIVE_FILTER, type OEPReviewStatus } from 'agu_oep_core';
import type {
    FeedbackPlatformConversationMessageEntity, FeedbackPlatformReviewEntity, FeedbackPlatformReviewResponseEntity,
} from 'mj_generatedentities';
import { OEPViewBase } from '../../state/oep-view-base';
import { OEPDataService } from '../../data/oep-data.service';
import { OEPFilesService } from '../../data/oep-files.service';
import { OEP_NOTE_MAX, type OEPStatusLine } from '../shared/status';
import { OEPPartialSaveError } from '../shared/partial-save';
import { OEP_TAB_BLOCKED, OEPOpenInNewTab } from '../shared/open-tab';
import { OEPLatestDrafts, OEPPresentationLinkSql, OEPUploadedFilesSql } from '../shared/files';
import { AsReviewStatus } from '../reviews/reviews.vm';
import {
    BuildReviewVM, CanSubmitReview, PartialSaveMessage, QuestionShortLabel,
    type OEPReviewAnswer, type OEPReviewQuestionInput, type ReviewInputs, type ReviewVM,
} from './review.vm';
import { DateOrNull, NumOrNull, Str, StrOrNull, Up, type OEPRow } from '../shared/rows';

/** logic.js:681 saveLabel shows "Saved ✓" after a save; the brief holds it for 3 s. */
export const OEP_SAVED_FLASH_MS = 3000;
const REVIEW_FORM = `FormType='Review' AND SeasonID IS NULL AND Status='Active'`;

/**
 * Only a signed https URL from our own resolver (OEPFilesService.DownloadUrl) is ever trusted as an
 * iframe resource; anything else is refused (never built from an object name).
 */
export function OEPIsTrustableDraftUrl(url: string): boolean {
    return url.startsWith('https://');
}

/**
 * Reviewer feedback form ("Review draft presentations" → one assignment) — markup.html:556-661.
 * All copy and rules live in review.vm.ts (pure, spec'd). Load gathers ReviewInputs in ONE RunViews
 * batch (OEPDataService.Batch, RLS-scoped to the reviewer, T5). Writes go through Act: the Review row
 * (SaveNew once, then Update), its Review Responses in parallel (Update or SaveNew), then the
 * Submitted status; the agu_oep_core entity rules (DecideReviewChange / DecideReviewResponseChange /
 * DecideThreadMessage) re-check windows and completeness on the server, and any refusal is shown in
 * the aria-live status. The draft preview is the resolver's signed URL (OEPFilesService.DownloadUrl).
 */
@Component({
    standalone: false,
    selector: 'oep-review',
    templateUrl: './oep-review.component.html',
    styleUrl: './oep-review.component.scss',
})
export class OEPReviewComponent extends OEPViewBase implements OnChanges {
    private readonly files = inject(OEPFilesService);
    private readonly sanitizer = inject(DomSanitizer);

    /** The Review Assignment (the `id=` of the `review` location). */
    @Input() AssignmentID: string | null = null;

    VM: ReviewVM | null = null;
    /** The assignment is not (or no longer) visible to the signed-in reviewer. */
    NotFound = false;
    /** The form's answers, keyed by ReviewQuestion ID (bound with ngModel). */
    Answers: Record<string, OEPReviewAnswer> = {};
    Reply = '';
    FormStatus: OEPStatusLine | null = null;
    ReplyStatus: OEPStatusLine | null = null;
    DraftStatus: OEPStatusLine | null = null;
    /** The signed preview URL, trusted only after OEPIsTrustableDraftUrl (PDF drafts only). */
    SafeDraftUrl: SafeResourceUrl | null = null;
    SavedFlash = false;
    readonly NoteMax = OEP_NOTE_MAX;

    private questions: OEPReviewQuestionInput[] = [];
    private inputs: Omit<ReviewInputs, 'JustSubmitted'> | null = null;
    private justSubmitted = false;
    private reviewID: string | null = null;
    private reviewStatus: OEPReviewStatus | null = null;
    /** ReviewQuestion ID (upper) → its Review Responses row ID, kept current as writes succeed (a retry updates, never duplicates). */
    private readonly responseIDs = new Map<string, string>();
    private rubricIDs = new Map<string, string>();
    private conversationID: string | null = null;
    private previewFileID: string | null = null;
    private flashTimer: ReturnType<typeof setTimeout> | null = null;
    private initialized = false;

    override ngOnInit(): void {
        this.initialized = true;
        super.ngOnInit();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized || !changes['AssignmentID'] || changes['AssignmentID'].isFirstChange()) return;
        this.reset();
        void this.Refresh();
    }

    override ngOnDestroy(): void {
        if (this.flashTimer !== null) clearTimeout(this.flashTimer);
        super.ngOnDestroy();
    }

    private reset(): void {
        this.VM = null;
        this.inputs = null;
        this.justSubmitted = false;
        this.reviewID = null;
        this.reviewStatus = null;
        this.responseIDs.clear();
        this.conversationID = null;
        this.previewFileID = null;
        this.SafeDraftUrl = null;
        this.Answers = {};
        this.Reply = '';
        this.FormStatus = this.ReplyStatus = this.DraftStatus = null;
    }

    protected async Load(): Promise<void> {
        const season = this.Context.Season;
        if (!season) return;
        if (!this.AssignmentID) {
            this.NotFound = true;
            this.VM = null;
            return;
        }
        const me = OEPDataService.G(this.Context.Me.UserID);
        const sid = OEPDataService.G(season.ID);
        const aid = OEPDataService.G(this.AssignmentID);
        const assignment = `ID='${aid}' AND ReviewerUserID='${me}' AND ${OEP_LIVE_FILTER.Assignment}`;
        const pres = `SELECT PresentationID FROM FeedbackPlatform.ReviewAssignment WHERE ${assignment}`;
        const review = `SELECT ID FROM FeedbackPlatform.Review WHERE ReviewAssignmentID='${aid}'`;
        const link = OEPPresentationLinkSql(pres);
        const [assignments, presentations, participants, fileRows, links, reviews, responses, questions, rubrics, conversations, messages] = await this.Data.Batch([
            { EntityName: OEP_ENTITY.ReviewAssignments, ExtraFilter: assignment, Fields: ['ID', 'PresentationID'] },
            { EntityName: OEP_ENTITY.Presentations, ExtraFilter: `SeasonID='${sid}' AND ${OEP_LIVE_FILTER.Presentation} AND ID IN (${pres})`, Fields: ['ID', 'Title', 'Section', 'Format', 'PresentationCode', 'PresenterUserID', 'PresenterUser'] },
            // The presenter's row (institution, career stage) — readable by the assigned reviewer (T5).
            { EntityName: OEP_ENTITY.Participants, ExtraFilter: `SeasonID='${sid}' AND UserID IN (SELECT PresenterUserID FROM FeedbackPlatform.Presentation WHERE ID IN (${pres}))`, Fields: ['UserID', 'Institution', 'CareerStage'] },
            { EntityName: OEP_ENTITY.Files, ExtraFilter: OEPUploadedFilesSql(pres, ['Draft']), Fields: ['ID', 'Name', 'Category', 'Status', 'ContentType', '__mj_CreatedAt'], OrderBy: '__mj_CreatedAt DESC' },
            { EntityName: OEP_ENTITY.FileEntityRecordLinks, ExtraFilter: link, Fields: ['FileID', 'RecordID'] },
            { EntityName: OEP_ENTITY.Reviews, ExtraFilter: `ReviewAssignmentID='${aid}'`, Fields: ['ID', 'Status', 'CompletedDate'] },
            { EntityName: OEP_ENTITY.ReviewResponses, ExtraFilter: `ReviewID IN (${review})`, Fields: ['ID', 'ReviewQuestionID', 'NumericValue', 'TextValue', 'RubricLevelID'] },
            { EntityName: OEP_ENTITY.ReviewQuestions, ExtraFilter: REVIEW_FORM, Fields: ['ID', 'Sequence', 'QuestionText', 'Description', 'IsRequired', 'ReviewQuestionType'], OrderBy: 'Sequence' },
            { EntityName: OEP_ENTITY.ReviewQuestionRubrics, ExtraFilter: `Status='Active' AND ReviewQuestionID IN (SELECT ID FROM FeedbackPlatform.ReviewQuestion WHERE ${REVIEW_FORM})`, Fields: ['ID', 'ReviewQuestionID', 'RatingValue', 'RatingLabel', 'Criteria'], OrderBy: 'Sequence' },
            { EntityName: OEP_ENTITY.Conversations, ExtraFilter: `ReviewID IN (${review})`, Fields: ['ID'] },
            { EntityName: OEP_ENTITY.ConversationMessages, ExtraFilter: `ThreadPosition IS NOT NULL AND ConversationID IN (SELECT ID FROM FeedbackPlatform.Conversation WHERE ReviewID IN (${review}))`, Fields: ['ConversationID', 'SenderUserID', 'MessageContent', 'ThreadPosition'], OrderBy: 'ThreadPosition' },
        ]);

        const a = assignments[0];
        const p = presentations[0];
        this.NotFound = a === undefined || p === undefined;
        if (!a || !p) {
            this.VM = null;
            this.inputs = null;
            return;
        }
        const presenter = participants.find((x) => Up(Str(x, 'UserID')) === Up(Str(p, 'PresenterUserID')));
        const r = reviews[0];
        this.reviewID = r ? Str(r, 'ID') : this.reviewID;
        this.reviewStatus = r ? AsReviewStatus(StrOrNull(r, 'Status')) : this.reviewStatus;
        for (const x of responses) this.responseIDs.set(Up(Str(x, 'ReviewQuestionID')), Str(x, 'ID'));

        this.questions = questions.map((q) => {
            const qid = Str(q, 'ID');
            return {
                ID: qid,
                Sequence: Number(q['Sequence']),
                Text: Str(q, 'QuestionText'),
                Description: StrOrNull(q, 'Description'),
                Required: q['IsRequired'] === true || q['IsRequired'] === 1,
                Rated: Str(q, 'ReviewQuestionType').startsWith('Rating Scale'),
                Levels: rubrics.filter((l) => Up(Str(l, 'ReviewQuestionID')) === Up(qid)).map((l) => ({
                    ID: Str(l, 'ID'), Value: Number(l['RatingValue']), Label: Str(l, 'RatingLabel'), Criteria: Str(l, 'Criteria'),
                })),
            };
        });
        this.rubricIDs = new Map(this.questions.flatMap((q) => q.Levels.map((l) => [`${Up(q.ID)}:${l.Value}`, l.ID] as const)));
        const levelOf = (x: OEPRow): number | null => {
            const id = StrOrNull(x, 'RubricLevelID');
            const byID = id ? rubrics.find((l) => Up(Str(l, 'ID')) === Up(id)) : undefined;
            return byID ? Number(byID['RatingValue']) : NumOrNull(x, 'NumericValue');
        };
        const saved: Record<string, OEPReviewAnswer> = {};
        for (const q of this.questions) {
            const x = responses.find((row) => Up(Str(row, 'ReviewQuestionID')) === Up(q.ID));
            saved[q.ID] = { Level: x && q.Rated ? levelOf(x) : null, Text: x ? (StrOrNull(x, 'TextValue') ?? '') : '' };
        }
        // The form starts from what is saved (a fresh copy — ngModel mutates it).
        this.Answers = Object.fromEntries(Object.entries(saved).map(([k, v]) => [k, { ...v }]));

        const conv = conversations[0];
        this.conversationID = conv ? Str(conv, 'ID') : null;
        const draft = OEPLatestDrafts(fileRows, links).get(Up(Str(p, 'ID'))) ?? null;

        this.inputs = {
            Phase: season.Phase, Timeline: season.Timeline, SeasonCode: season.Code, Me: me,
            Assignment: {
                ID: Str(a, 'ID'), Title: Str(p, 'Title'), SectionName: Str(p, 'Section'),
                Format: StrOrNull(p, 'Format'), Code: StrOrNull(p, 'PresentationCode'),
                PresenterName: Str(p, 'PresenterUser'),
                Institution: presenter ? StrOrNull(presenter, 'Institution') : null,
                CareerStage: presenter ? StrOrNull(presenter, 'CareerStage') : null,
            },
            Draft: draft,
            Questions: this.questions,
            Review: this.reviewID !== null && this.reviewStatus !== null
                ? { ID: this.reviewID, Status: this.reviewStatus, CompletedDate: r ? DateOrNull(r, 'CompletedDate') : null }
                : null,
            Saved: saved,
            Thread: this.conversationID === null ? [] : messages
                .filter((m) => Up(Str(m, 'ConversationID')) === Up(this.conversationID ?? ''))
                .map((m) => ({ SenderUserID: Str(m, 'SenderUserID'), Body: Str(m, 'MessageContent') })),
            ConversationID: this.conversationID,
            Resources: this.Context.Resources.reviewer ?? [],
        };
        this.rebuild();
        await this.preparePreview();
    }

    private rebuild(): void {
        this.VM = this.inputs ? BuildReviewVM({ ...this.inputs, JustSubmitted: this.justSubmitted }) : null;
    }

    /** Signs the preview once per draft file (a save's reload keeps the same iframe). */
    private async preparePreview(): Promise<void> {
        const d = this.VM?.Edit?.Draft ?? null;
        if (d === null || !d.IsPdf) {
            this.SafeDraftUrl = null;
            this.previewFileID = null;
            return;
        }
        if (this.previewFileID !== null && Up(this.previewFileID) === Up(d.FileID)) return;
        try {
            const url = await this.files.DownloadUrl(d.FileID);
            this.SafeDraftUrl = OEPIsTrustableDraftUrl(url) ? this.sanitizer.bypassSecurityTrustResourceUrl(url) : null;
            this.previewFileID = d.FileID;
        } catch (err) {
            this.SafeDraftUrl = null;
            this.DraftStatus = { Text: err instanceof Error ? err.message : String(err), Tone: 'error' };
        }
    }

    Answer(questionID: string): OEPReviewAnswer {
        this.Answers[questionID] ??= { Level: null, Text: '' };
        return this.Answers[questionID];
    }

    Pick(questionID: string, level: number): void {
        this.Answer(questionID).Level = level;
        this.Changed();
    }

    get CanSubmit(): boolean {
        return this.VM?.Mode === 'editing' && !this.Busy && CanSubmitReview(this.questions, this.Answers);
    }

    get SaveLabel(): string {
        return this.SavedFlash ? 'Saved ✓' : 'Save and finish later';
    }

    /** Save (and, with `submit`, submit) the form. Mirrors DecideReviewChange: only while editing. */
    async Save(submit: boolean): Promise<void> {
        const aid = this.AssignmentID;
        if (aid === null || this.VM?.Mode !== 'editing' || this.Busy) return;
        if (submit && !CanSubmitReview(this.questions, this.Answers)) return;
        this.FormStatus = null;
        const answers = Object.fromEntries(this.questions.map((q) => [q.ID, { ...this.Answer(q.ID) }]));
        let partial: OEPPartialSaveError | null = null;
        const ok = await this.Act(async () => {
            let rid = this.reviewID;
            if (rid === null) {
                const r = await this.Data.SaveNew<FeedbackPlatformReviewEntity>(OEP_ENTITY.Reviews, (e) => {
                    e.ReviewAssignmentID = aid; e.Status = 'InProgress'; e.StartedDate = new Date();
                });
                rid = r.ID;
                this.reviewID = r.ID;
                this.reviewStatus = 'InProgress';
            } else if (this.reviewStatus === 'NotStarted') {
                await this.Data.Update<FeedbackPlatformReviewEntity>(OEP_ENTITY.Reviews, rid, (e) => { e.Status = 'InProgress'; e.StartedDate ??= new Date(); });
                this.reviewStatus = 'InProgress';
            }
            const reviewID = rid;
            try {
                await this.saveResponses(reviewID, answers);
            } catch (err) {
                if (err instanceof OEPPartialSaveError) partial = err;
                throw err; // submit is never attempted after a refused answer
            }
            if (submit) {
                await this.Data.Update<FeedbackPlatformReviewEntity>(OEP_ENTITY.Reviews, reviewID, (e) => { e.Status = 'Submitted'; e.CompletedDate = new Date(); });
                this.reviewStatus = 'Submitted';
                this.justSubmitted = true;
            }
        }, null, (m) => { this.FormStatus = { Text: m, Tone: 'error' }; });
        if (!ok) {
            const failed = partial as OEPPartialSaveError | null; // assigned inside the Act closure, which TS's flow analysis cannot see
            if (failed !== null) {
                // The other answers were saved: reload so the form reflects the server, then put back what
                // the reviewer typed for the refused ones (the reload would otherwise drop it).
                await this.Refresh();
                for (const id of failed.FailedQuestionIDs) if (answers[id]) this.Answers[id] = { ...answers[id] };
            }
            this.Changed();
            return;
        }
        if (submit) {
            this.rebuild();
            this.FormStatus = null;
        } else {
            this.FormStatus = { Text: 'Saved ✓', Tone: 'ok' }; // logic.js:681
            this.flashSaved();
        }
        this.Changed();
    }

    /** Every answered (or previously saved) question in parallel; all settle before a failure is reported. */
    private async saveResponses(reviewID: string, answers: Readonly<Record<string, OEPReviewAnswer>>): Promise<void> {
        const attempted: OEPReviewQuestionInput[] = [];
        const work = this.questions.flatMap((q) => {
            const a = answers[q.ID] ?? { Level: null, Text: '' };
            const text = a.Text.trim();
            const level = q.Rated ? a.Level : null;
            const existing = this.responseIDs.get(Up(q.ID)) ?? null;
            if (existing === null && level === null && text.length === 0) return [];
            attempted.push(q);
            const rubricLevelID = level === null ? null : (this.rubricIDs.get(`${Up(q.ID)}:${level}`) ?? null);
            const fill = (e: FeedbackPlatformReviewResponseEntity): void => {
                e.NumericValue = level; e.TextValue = text.length > 0 ? text : null; e.RubricLevelID = rubricLevelID;
            };
            return [existing !== null
                ? this.Data.Update<FeedbackPlatformReviewResponseEntity>(OEP_ENTITY.ReviewResponses, existing, fill).then(() => undefined)
                : this.Data.SaveNew<FeedbackPlatformReviewResponseEntity>(OEP_ENTITY.ReviewResponses, (e) => { e.ReviewID = reviewID; e.ReviewQuestionID = q.ID; fill(e); })
                    .then((row) => { this.responseIDs.set(Up(q.ID), row.ID); })];
        });
        const results = await Promise.allSettled(work);
        const failed = results.flatMap((x, i) => (x.status === 'rejected' ? [{ Question: attempted[i], Reason: x.reason }] : []));
        if (failed.length === 0) return;
        const first = failed[0].Reason;
        const message = first instanceof Error ? first.message : String(first);
        throw new OEPPartialSaveError(
            PartialSaveMessage(failed.map((f) => QuestionShortLabel(f.Question.Sequence)), attempted.length, message),
            failed.map((f) => f.Question.ID),
        );
    }

    private flashSaved(): void {
        this.SavedFlash = true;
        if (this.flashTimer !== null) clearTimeout(this.flashTimer);
        this.flashTimer = setTimeout(() => {
            this.SavedFlash = false;
            this.flashTimer = null;
            this.Changed();
        }, OEP_SAVED_FLASH_MS);
    }

    /** Trimmed reply when it is sendable (1..NoteMax characters), else null. */
    private sendable(): string | null {
        const t = this.Reply.trim();
        return t.length > 0 && t.length <= OEP_NOTE_MAX ? t : null;
    }

    get CanSendReply(): boolean {
        return (this.VM?.Archive?.CanReply ?? false) && this.conversationID !== null && !this.Busy && this.sendable() !== null;
    }

    /** Reply into the presenter's conversation (reviewers never create one — T5). */
    async SendReply(): Promise<void> {
        const body = this.sendable();
        const conversationID = this.conversationID;
        if (body === null || conversationID === null || !this.VM?.Archive?.CanReply) return;
        const me = this.Context.Me.UserID;
        this.ReplyStatus = null;
        const ok = await this.Act(async () => {
            await this.Data.SaveNew<FeedbackPlatformConversationMessageEntity>(OEP_ENTITY.ConversationMessages, (m) => {
                m.ConversationID = conversationID; m.SenderUserID = me; m.MessageContent = body; m.SentDate = new Date(); m.Status = 'Sent'; m.MessageType = 'Message';
            });
        }, null, (m) => { this.ReplyStatus = { Text: m, Tone: 'error' }; });
        if (ok) {
            this.Reply = '';
            this.ReplyStatus = { Text: 'Reply sent.', Tone: 'ok' };
        }
        this.Changed();
    }

    async OpenDraft(): Promise<void> {
        const d = this.VM?.Edit?.Draft ?? null;
        if (d === null) return;
        this.DraftStatus = null;
        try {
            const tab = await OEPOpenInNewTab(() => this.files.DownloadUrl(d.FileID));
            if (tab.Blocked) this.DraftStatus = { Text: OEP_TAB_BLOCKED, Tone: 'error', Link: { Label: 'Open the draft', Url: tab.Url } };
        } catch (err) {
            this.DraftStatus = { Text: err instanceof Error ? err.message : String(err), Tone: 'error' };
        }
        this.Changed();
    }

    Back(): void {
        this.Go('reviews');
    }
}
